import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, StateBadge, fmt, btnPrimary, btnOutline, Empty } from "@/components/portal/PortalUI";
import { ClipboardCheck, Timer, ArrowLeft } from "lucide-react";

const QuizRunner = ({ aid, onExit }) => {
  const [q, setQ] = useState(null);
  const [att, setAtt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [left, setLeft] = useState(null);

  useEffect(() => { api.get(`/student/quizzes/${aid}`).then((r) => { setQ(r.data); if (r.data.open_attempt) setAtt(r.data.open_attempt); }); }, [aid]);
  useEffect(() => {
    if (!att || !q?.quiz.time_limit_minutes || result) return;
    const end = new Date(att.started_at).getTime() + q.quiz.time_limit_minutes * 60000;
    const t = setInterval(() => setLeft(Math.max(0, Math.round((end - Date.now()) / 1000))), 1000);
    return () => clearInterval(t);
  }, [att, q, result]);

  const start = async () => { try { const r = await api.post(`/student/quizzes/${aid}/start`); setAtt(r.data); } catch (e) { toast.error(errMsg(e)); } };
  const pick = (qq, cid) => setAnswers((a) => ({ ...a, [qq.qid]: qq.type === "ms" ? ((a[qq.qid] || []).includes(cid) ? a[qq.qid].filter((x) => x !== cid) : [...(a[qq.qid] || []), cid]) : [cid] }));
  const submit = async () => {
    const missing = q.quiz.questions.filter((qq) => !(answers[qq.qid] || []).length).length;
    if (missing && !window.confirm(`${missing} question(s) unanswered. Submit anyway?`)) return;
    try { const r = await api.post(`/student/quizzes/${aid}/submit`, { attempt_id: att.attempt_id, answers }); setResult(r.data); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!q) return <p className="text-dark-text/50">Loading quiz…</p>;
  const used = q.attempts.length;
  return (
    <div className="space-y-5" data-testid="quiz-runner">
      <button onClick={onExit} className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-magenta" data-testid="quiz-back"><ArrowLeft size={16} /> Back to quizzes</button>
      <Card title={q.quiz.title}>
        <p className="text-sm whitespace-pre-wrap text-dark-text/75">{q.quiz.instructions}</p>
        <p className="text-xs text-dark-text/50 mt-3">{q.quiz.questions.length} questions · 100 points · Passing score {q.quiz.passing_score} · Attempts {used}/{q.attempts_allowed}{q.quiz.time_limit_minutes ? ` · ${q.quiz.time_limit_minutes} min limit` : ""}</p>
        {q.attempts.length > 0 && <ul className="mt-3 text-xs space-y-1">{q.attempts.map((a, i) => <li key={a.attempt_id}>Attempt {i + 1}: {fmt(a.submitted_at)}{a.score !== undefined && ` — ${a.score}/100 (${a.passed ? "Pass" : "Fail"})`}</li>)}</ul>}
      </Card>
      {result ? (
        <Card testid="quiz-result">
          {result.delayed ? <p className="text-navy font-semibold">{result.message}</p> : (
            <div className="text-center py-6">
              <p className="text-xs uppercase tracking-widest text-dark-text/50">Your score</p>
              <p className="font-display text-6xl font-black text-navy" data-testid="quiz-score">{result.score}<span className="text-2xl text-dark-text/40">/100</span></p>
              <p className={`mt-3 inline-block px-5 py-1.5 rounded-full font-black text-lg ${result.passed ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid="quiz-pass-fail">{result.result}</p>
              <p className="text-xs text-dark-text/50 mt-2">Passing score: {result.passing_score}{result.time_exceeded ? " · Time limit exceeded" : ""}</p>
            </div>
          )}
        </Card>
      ) : !att ? (
        <button onClick={start} disabled={used >= q.attempts_allowed} className={btnPrimary} data-testid="quiz-start">{used >= q.attempts_allowed ? "No attempts remaining" : "Start Quiz"}</button>
      ) : (
        <>
          {left !== null && <div className="sticky top-2 z-10 inline-flex items-center gap-2 bg-navy text-white text-sm font-bold px-4 py-2 rounded-full" data-testid="quiz-timer"><Timer size={16} /> {Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}</div>}
          {q.quiz.questions.map((qq, i) => (
            <Card key={qq.qid} testid={`quiz-question-${i}`}>
              <p className="font-semibold text-navy">{i + 1}. {qq.text} <span className="text-xs font-normal text-dark-text/40">({qq.points} pts{qq.type === "ms" ? " · select all that apply" : ""})</span></p>
              <div className="mt-3 grid gap-2">
                {qq.choices.map((c) => {
                  const on = (answers[qq.qid] || []).includes(c.cid);
                  return (
                    <button type="button" key={c.cid} onClick={() => pick(qq, c.cid)} data-testid={`quiz-choice-${i}-${c.cid}`}
                      className={`text-left text-sm px-4 py-2.5 rounded-md border-2 transition-colors ${on ? "border-magenta bg-magenta-light text-navy font-semibold" : "border-navy/10 hover:border-navy/30"}`}>{c.text}</button>
                  );
                })}
              </div>
            </Card>
          ))}
          <button onClick={submit} className={btnPrimary} data-testid="quiz-submit">Submit Quiz</button>
        </>
      )}
    </div>
  );
};

export const StudentQuizzes = ({ quizzes, reload }) => {
  const [active, setActive] = useState(null);
  if (active) return <QuizRunner aid={active} onExit={() => { setActive(null); reload(); }} />;
  return (
    <Card title="Assigned Quizzes" testid="student-quizzes">
      {quizzes.length === 0 ? <Empty>No quizzes assigned yet.</Empty> : (
        <div className="grid md:grid-cols-2 gap-4">
          {quizzes.map((q) => (
            <div key={q.assignment_id} className="border border-navy/10 rounded-lg p-4" data-testid={`quiz-card-${q.assignment_id}`}>
              <div className="flex justify-between gap-2"><h4 className="font-display font-bold text-navy flex items-center gap-2"><ClipboardCheck size={18} className="text-magenta" /> {q.title}</h4><StateBadge state={q.passed === true ? "Passed" : q.passed === false ? "Failed" : q.state} /></div>
              <p className="text-xs text-dark-text/50 mt-1">{q.course || q.program} · {q.question_count} questions · Pass {q.passing_score}/100 · Due {fmt(q.due_date)}</p>
              <p className="text-sm mt-2">Attempts {q.attempts_used}/{q.attempts_allowed}{q.best_score !== null && q.best_score !== undefined && <> · Best score <strong data-testid={`quiz-best-${q.assignment_id}`}>{q.best_score}/100</strong></>}</p>
              <button onClick={() => setActive(q.assignment_id)} className={`${q.attempts_used ? btnOutline : btnPrimary} mt-3`} data-testid={`quiz-open-${q.assignment_id}`}>{q.attempts_used ? "View / Retake" : "Take Quiz"}</button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
